/**
 * Campaign level-map — recital journey with lock / clear / current states.
 */

export type StageNodeState = "locked" | "current" | "cleared";

export interface StageNode {
  id: number;
  title: string;
  state: StageNodeState;
  classesDone: number;
  classesTotal: number;
}

/** Horizontal ribbons remain unreadable through tablet widths with 9 stages. */
const COMPACT_BREAKPOINT = 840;

export class LevelMapCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private nodes: StageNode[] = [];
  private selected = 1;
  private t0 = performance.now();
  private raf = 0;
  private running = false;
  private lastLayoutKey = "";
  onSelect: ((stageId: number) => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.resize();
    this.canvas.addEventListener("click", (e) => this.hit(e));
    window.addEventListener("resize", () => this.resize());
  }

  setNodes(nodes: StageNode[], selected: number): void {
    this.nodes = nodes;
    this.selected = selected;
    this.resize();
    this.start();
  }

  private isCompact(width: number): boolean {
    return width < COMPACT_BREAKPOINT;
  }

  private fitText(text: string, maxWidth: number): string {
    if (this.ctx.measureText(text).width <= maxWidth) return text;
    let out = text;
    while (out.length > 1 && this.ctx.measureText(`${out}…`).width > maxWidth) {
      out = out.slice(0, -1);
    }
    return `${out}…`;
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    const cssW = parent?.clientWidth || 900;
    const compact = this.isCompact(cssW);
    // A horizontal nine-node ribbon is unreadable on phones/tablets. Give every
    // module its own row and let the normal page scroll vertically.
    const cssH = compact
      ? Math.max(520, this.nodes.length * 96 + 32)
      : Math.min(420, Math.max(280, cssW * 0.42));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const layoutKey = `${cssW}|${cssH}|${dpr}|${this.nodes.length}|${compact ? 1 : 0}`;
    if (layoutKey === this.lastLayoutKey) return;
    this.lastLayoutKey = layoutKey;
    this.dpr = dpr;
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      if (!this.running) return;
      this.draw((performance.now() - this.t0) / 1000);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private layout(w: number, h: number) {
    const n = Math.max(this.nodes.length, 1);
    const compact = this.isCompact(w);
    return this.nodes.map((node, i) => {
      if (compact) {
        const rowH = (h - 32) / n;
        return {
          node,
          x: 48,
          y: 16 + rowH * (i + 0.5),
          r: 23,
          compact,
          maxLabelWidth: Math.max(120, w - 98),
          labelAbove: false,
        };
      }
      // Zigzag rows: upper nodes label above, lower nodes label below, so each
      // label owns two node-spacings of width and never meets its neighbours.
      const t = n === 1 ? 0.5 : i / (n - 1);
      const x = w * (0.07 + t * 0.86);
      const spacing = n === 1 ? w : (w * 0.86) / (n - 1);
      const labelAbove = i % 2 === 1;
      const y = h * (labelAbove ? 0.36 : 0.62);
      const r = Math.max(20, Math.min(28, spacing * 0.36));
      const maxLabelWidth = Math.max(80, Math.min(170, spacing * 2 - 14));
      return { node, x, y, r, compact, maxLabelWidth, labelAbove };
    });
  }

  /** Word-wrap into at most `maxLines`, ellipsising the last line if needed. */
  private wrapText(text: string, maxWidth: number, maxLines: number): string[] {
    const lines: string[] = [];
    const words = text.split(/\s+/);
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const last = lines[lines.length - 1];
      const candidate = last ? `${last} ${word}` : word;
      if (last && this.ctx.measureText(candidate).width > maxWidth) {
        if (lines.length === maxLines) {
          lines[lines.length - 1] = this.fitText(`${last} ${words.slice(i).join(" ")}`, maxWidth);
          return lines;
        }
        lines.push(this.fitText(word, maxWidth));
      } else if (last) {
        lines[lines.length - 1] = candidate;
      } else {
        lines.push(this.fitText(word, maxWidth));
      }
    }
    return lines;
  }

  private hit(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const pts = this.layout(rect.width, rect.height);
    for (const p of pts) {
      const d = Math.hypot(x - p.x, y - p.y);
      if (d <= p.r + 8 && p.node.state !== "locked") {
        this.selected = p.node.id;
        this.onSelect?.(p.node.id);
        break;
      }
    }
  }

  /** Sample points along the curved stage path for ribbon drawing. */
  private pathPoints(
    pts: ReturnType<LevelMapCanvas["layout"]>,
    samples: number,
  ): { x: number; y: number }[] {
    if (pts.length < 2) return pts.map((p) => ({ x: p.x, y: p.y }));
    const out: { x: number; y: number }[] = [];
    for (let s = 0; s <= samples; s++) {
      const u = s / samples;
      const seg = u * (pts.length - 1);
      const i = Math.min(Math.floor(seg), pts.length - 2);
      const local = seg - i;
      const p0 = pts[i];
      const p1 = pts[i + 1];
      const midX = (p0.x + p1.x) / 2;
      const midY = (p0.y + p1.y) / 2;
      const t = local;
      const x = (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * midX + t * t * p1.x;
      const y = (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * midY + t * t * p1.y;
      out.push({ x, y });
    }
    return out;
  }

  private drawConcertHallBg(w: number, h: number, t: number): void {
    const ctx = this.ctx;

    const wood = ctx.createLinearGradient(0, 0, w, h);
    wood.addColorStop(0, "#2a1810");
    wood.addColorStop(0.35, "#3d2418");
    wood.addColorStop(0.65, "#2e1a12");
    wood.addColorStop(1, "#140c08");
    ctx.fillStyle = wood;
    ctx.fillRect(0, 0, w, h);

    const spotlight = ctx.createRadialGradient(w * 0.5, h * 0.15, 8, w * 0.5, h * 0.45, w * 0.65);
    spotlight.addColorStop(0, "rgba(255, 210, 150, 0.14)");
    spotlight.addColorStop(0.45, "rgba(180, 120, 70, 0.06)");
    spotlight.addColorStop(1, "transparent");
    ctx.fillStyle = spotlight;
    ctx.fillRect(0, 0, w, h);

    const floor = ctx.createLinearGradient(0, h * 0.55, 0, h);
    floor.addColorStop(0, "rgba(60, 35, 22, 0.15)");
    floor.addColorStop(1, "rgba(20, 10, 6, 0.55)");
    ctx.fillStyle = floor;
    ctx.fillRect(0, h * 0.5, w, h * 0.5);

    ctx.save();
    ctx.globalAlpha = 0.07;
    ctx.strokeStyle = "rgba(240, 220, 190, 0.9)";
    ctx.lineWidth = 1;
    for (let staff = 0; staff < 3; staff++) {
      const baseY = h * (0.12 + staff * 0.22);
      for (let line = 0; line < 5; line++) {
        ctx.beginPath();
        ctx.moveTo(0, baseY + line * 6);
        ctx.lineTo(w, baseY + line * 6);
        ctx.stroke();
      }
    }
    ctx.restore();

    const shimmerX = ((t * 0.08) % 1.4 - 0.2) * w;
    const shimmer = ctx.createLinearGradient(shimmerX - 80, 0, shimmerX + 80, h);
    shimmer.addColorStop(0, "transparent");
    shimmer.addColorStop(0.5, "rgba(255, 230, 190, 0.04)");
    shimmer.addColorStop(1, "transparent");
    ctx.fillStyle = shimmer;
    ctx.fillRect(0, 0, w, h);

    const vignette = ctx.createRadialGradient(w / 2, h / 2, h * 0.15, w / 2, h / 2, w * 0.75);
    vignette.addColorStop(0, "transparent");
    vignette.addColorStop(1, "rgba(0, 0, 0, 0.45)");
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);
  }

  private drawKeyboardRibbon(
    path: { x: number; y: number }[],
    t: number,
  ): void {
    if (path.length < 2) return;
    const ctx = this.ctx;

    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.strokeStyle = "rgba(18, 12, 8, 0.85)";
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
    ctx.stroke();

    ctx.strokeStyle = "rgba(255, 248, 235, 0.92)";
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
    ctx.stroke();

    ctx.strokeStyle = "rgba(200, 180, 150, 0.35)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
    ctx.stroke();

    const tickEvery = 3;
    for (let i = 0; i < path.length; i += tickEvery) {
      const p = path[i];
      const prev = path[Math.max(0, i - 1)];
      const next = path[Math.min(path.length - 1, i + 1)];
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const tickLen = 5 + (i % 7 === 0 ? 2 : 0);
      ctx.strokeStyle = "rgba(80, 60, 45, 0.55)";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(p.x - nx * tickLen, p.y - ny * tickLen);
      ctx.lineTo(p.x + nx * tickLen, p.y + ny * tickLen);
      ctx.stroke();
    }

    const glowPos = ((t * 0.12) % 1) * (path.length - 1);
    const gi = Math.floor(glowPos);
    const gl = glowPos - gi;
    const gp0 = path[gi];
    const gp1 = path[Math.min(gi + 1, path.length - 1)];
    const gx = gp0.x + (gp1.x - gp0.x) * gl;
    const gy = gp0.y + (gp1.y - gp0.y) * gl;
    const pulse = 0.5 + 0.5 * Math.sin(t * 2.5);
    const rg = ctx.createRadialGradient(gx, gy, 0, gx, gy, 28 + pulse * 8);
    rg.addColorStop(0, "rgba(47, 230, 176, 0.18)");
    rg.addColorStop(1, "transparent");
    ctx.fillStyle = rg;
    ctx.beginPath();
    ctx.arc(gx, gy, 36, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawLockGlyph(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(x, y);
    ctx.strokeStyle = "rgba(140, 150, 160, 0.85)";
    ctx.fillStyle = "rgba(100, 110, 120, 0.5)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(-size * 0.22, -size * 0.05, size * 0.44, size * 0.38, 3);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, -size * 0.12, size * 0.18, Math.PI, 0);
    ctx.stroke();
    ctx.restore();
  }

  private drawGoldSeal(x: number, y: number, r: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(x, y);
    ctx.strokeStyle = "rgba(255, 220, 120, 0.7)";
    ctx.lineWidth = 1.5;
    const spikes = 12;
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const ang = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
      const rad = i % 2 === 0 ? r + 5 : r + 1;
      const px = Math.cos(ang) * rad;
      const py = Math.sin(ang) * rad;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.fillStyle = "rgba(240, 193, 90, 0.35)";
    ctx.fill();
    ctx.restore();
  }

  private drawMedallion(
    p: ReturnType<LevelMapCanvas["layout"]>[number],
    selected: boolean,
    t: number,
  ): void {
    const ctx = this.ctx;
    const isCurrent = p.node.state === "current";
    const breathe = isCurrent ? 1 + 0.05 * Math.sin(t * 3.2) : 1;
    const r = p.r * breathe;

    if (p.node.state !== "locked") {
      const glowColor =
        p.node.state === "cleared"
          ? "rgba(240, 193, 90, 0.22)"
          : "rgba(47, 230, 176, 0.25)";
      ctx.beginPath();
      ctx.fillStyle = glowColor;
      ctx.arc(p.x, p.y, r + 16, 0, Math.PI * 2);
      ctx.fill();
    }

    if (p.node.state === "cleared") {
      this.drawGoldSeal(p.x, p.y, r + 2);
    }

    const g = ctx.createRadialGradient(p.x - 8, p.y - 10, 4, p.x, p.y, r);
    if (p.node.state === "locked") {
      g.addColorStop(0, "#3a3a42");
      g.addColorStop(0.5, "#222228");
      g.addColorStop(1, "#0e0e12");
    } else if (p.node.state === "cleared") {
      g.addColorStop(0, "#ffe8a8");
      g.addColorStop(0.45, "#d4a840");
      g.addColorStop(1, "#8a6820");
    } else {
      g.addColorStop(0, "#8affd4");
      g.addColorStop(0.4, "#2ecc96");
      g.addColorStop(1, "#14805a");
    }
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle =
      p.node.state === "locked"
        ? "rgba(80, 85, 95, 0.6)"
        : selected
          ? "rgba(255, 255, 255, 0.95)"
          : p.node.state === "cleared"
            ? "rgba(255, 230, 160, 0.7)"
            : "rgba(180, 255, 220, 0.55)";
    ctx.lineWidth = selected ? 3 : 2;
    ctx.stroke();

    if (p.node.state === "locked") {
      this.drawLockGlyph(p.x, p.y - 2, r * 0.9);
    } else {
      ctx.fillStyle = p.node.state === "cleared" ? "#2a1808" : "#04140f";
      ctx.font = `700 ${Math.round(r * 0.72)}px Oxanium, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(p.node.id), p.x, p.y + 1);
    }

    const title = p.node.title?.trim() || `Stage ${p.node.id}`;
    const tag =
      p.node.state === "locked"
        ? "LOCKED"
        : p.node.state === "cleared"
          ? "CLEARED"
          : `${p.node.classesDone}/${p.node.classesTotal}`;
    const titleColor = p.node.state === "locked" ? "rgba(130, 135, 145, 0.75)" : "#e8f0ec";
    const tagColor =
      p.node.state === "locked" ? "rgba(120, 125, 135, 0.7)" : "rgba(200, 230, 220, 0.65)";

    ctx.textBaseline = "top";
    if (p.compact) {
      const labelX = p.x + r + 18;
      const titleY = p.y - 22;
      ctx.font = "700 12px Sora, sans-serif";
      ctx.textAlign = "left";
      const lines = this.wrapText(title, p.maxLabelWidth, 3);
      ctx.fillStyle = titleColor;
      lines.forEach((line, index) => ctx.fillText(line, labelX, titleY + index * 15));
      ctx.fillStyle = tagColor;
      ctx.font = "11px Sora, sans-serif";
      ctx.fillText(`MODULE ${p.node.id} · ${tag}`, labelX, titleY + lines.length * 15 + 3);
      return;
    }

    const lineH = 13;
    const tagH = 12;
    ctx.font = "600 11px Sora, sans-serif";
    const lines = this.wrapText(title, p.maxLabelWidth, 2);
    const blockH = lines.length * lineH + 3 + tagH;
    const w = this.canvas.width / this.dpr;
    const half = p.maxLabelWidth / 2;
    const labelX = Math.max(half + 6, Math.min(w - half - 6, p.x));
    const gap = 12;
    const top = p.labelAbove ? p.y - r - gap - blockH : p.y + r + gap;

    ctx.textAlign = "center";
    ctx.shadowColor = "rgba(0, 0, 0, 0.75)";
    ctx.shadowBlur = 4;
    ctx.fillStyle = titleColor;
    lines.forEach((line, index) => ctx.fillText(line, labelX, top + index * lineH));
    ctx.fillStyle = tagColor;
    ctx.font = "10px Sora, sans-serif";
    ctx.fillText(tag, labelX, top + lines.length * lineH + 3);
    ctx.shadowBlur = 0;
    ctx.shadowColor = "transparent";
  }

  private draw(t: number): void {
    const w = this.canvas.width / this.dpr;
    const h = this.canvas.height / this.dpr;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);

    this.drawConcertHallBg(w, h, t);

    const pts = this.layout(w, h);
    if (pts.length > 1) {
      const path = this.pathPoints(pts, 80);
      this.drawKeyboardRibbon(path, t);
    }

    for (const p of pts) {
      this.drawMedallion(p, p.node.id === this.selected, t);
    }
  }
}
